/**
 * Discount Ninja bridge.
 *
 * Discount Ninja owns all pricing. This file never calculates a discount — it
 * only marks the elements DN cannot find on its own and tells DN when the DOM
 * has changed. Anything DN displays is what DN will charge, so display and
 * checkout cannot drift.
 *
 * Horizon price markup is tagged in snippets/price.liquid. This file handles
 * dynamic product prices and any configured page builder price nodes.
 *
 * DN contract (v10):
 *   [data-la-dn-price]                     element DN replaces
 *   [data-la-dn-product-handle|-id]        product container around it
 *   [data-la-dn-product-variant]           selected variant
 *   la:dn:collection:updated               re-scan after the DOM changes
 */
(function () {
  'use strict';

  var config = window.VIPPricingConfig || {};
  var TAGGED = 'data-la-dn-price';
  var REPLACED = 'limoniapps-discountninja-productprice-replaced';

  /* ------------------------------------------------------------- tagging */

  /**
   * Marks builder-rendered price nodes so DN will replace them.
   *
   * Only the price element is tagged; DN reads the product from the container
   * attributes rendered in Liquid. Tagging is idempotent — DN keys off the
   * attribute, and re-adding it to an already-tagged node is a no-op.
   */
  function tagPrices(root) {
    var selector = (config.selectors || '').trim();
    if (!selector) return 0;

    var scope = root && root.querySelectorAll ? root : document;
    var found;

    try {
      found = scope.querySelectorAll(selector);
    } catch (e) {
      if (window.console && console.warn) {
        console.warn('[vip-pricing] Invalid price selector: ' + selector, e);
      }
      return 0;
    }

    var tagged = 0;

    for (var i = 0; i < found.length; i++) {
      var el = found[i];

      // Never tag something DN has already rewritten — that would hand it its
      // own output as if it were the original price.
      if (el.hasAttribute(TAGGED)) continue;
      if (el.className && String(el.className).indexOf(REPLACED) !== -1) continue;

      el.setAttribute(TAGGED, '');
      tagged++;
    }

    return tagged;
  }

  /** Keeps the container's variant attribute in step with the page. */
  function syncVariant() {
    var id = new URLSearchParams(window.location.search).get('variant');
    if (!id) return false;
    var productId = String(config.productId || '');
    if (!productId) return false;
    var containers = document.querySelectorAll('[data-la-dn-product-id]');
    var changed = false;
    for (var i = 0; i < containers.length; i++) {
      if (containers[i].getAttribute('data-la-dn-product-id') !== productId) continue;
      if (containers[i].getAttribute('data-la-dn-product-variant') === id) continue;
      containers[i].setAttribute('data-la-dn-product-variant', id);
      changed = true;
    }
    return changed;
  }

  /** Asks Discount Ninja to re-scan. Named by DN, not by us. */
  function notifyDiscountNinja() {
    try {
      document.dispatchEvent(new CustomEvent('la:dn:collection:updated'));
    } catch (e) {
      // Older browsers without the CustomEvent constructor.
      var evt = document.createEvent('CustomEvent');
      evt.initCustomEvent('la:dn:collection:updated', true, true, null);
      document.dispatchEvent(evt);
    }
  }

  /* --------------------------------------------------- minimum-order note */

  /**
   * Contract pricing carries a minimum order per product, so the page says so
   * beside the price — but only once DN has actually applied a discount.
   * A replaced price is the signal; there is no guessing about session state.
   */
  function syncNote() {
    var text = config.minQuantityMessage;
    if (!text) return;

    var existing = document.querySelectorAll('[data-vip-note]');
    var i;

    for (i = 0; i < existing.length; i++) {
      var previous = existing[i].previousElementSibling;
      if (!config.productId || !previous || (!previous.classList.contains(REPLACED) && !previous.querySelector('.' + REPLACED))) {
        existing[i].remove();
      }
    }

    if (!config.productId) return;
    var anchors = document.querySelectorAll('product-price [data-la-dn-product-id="' + config.productId + '"] [' + TAGGED + ']');

    for (i = 0; i < anchors.length; i++) {
      var anchor = anchors[i];
      if (!anchor.parentNode) continue;
      if (!anchor.classList.contains(REPLACED) && !anchor.querySelector('.' + REPLACED)) continue;
      if (anchor.nextElementSibling && anchor.nextElementSibling.hasAttribute('data-vip-note')) continue;

      var note = document.createElement('div');
      note.className = 'vip-min-qty-note';
      note.setAttribute('data-vip-note', '');
      note.textContent = text;
      anchor.parentNode.insertBefore(note, anchor.nextSibling);
    }
  }

  /* ------------------------------------------------------------- observer */

  var scheduled = false;
  var pendingCollectionCards = false;

  function containsDiscountNinjaCard(node) {
    if (!(node instanceof Element)) return false;

    return (
      node.matches('product-card[data-la-dn-product-id]') ||
      node.querySelector('product-card[data-la-dn-product-id]') !== null
    );
  }

  function refresh() {
    var tagged = tagPrices(document);
    var variantChanged = syncVariant();
    var collectionCardsAdded = pendingCollectionCards;
    pendingCollectionCards = false;

    if (tagged || variantChanged || collectionCardsAdded) notifyDiscountNinja();

    syncNote();
  }

  function schedule() {
    if (scheduled) return;
    scheduled = true;
    window.requestAnimationFrame(function () {
      scheduled = false;
      refresh();
    });
  }

  function observe() {
    if (!window.MutationObserver) return;

    new MutationObserver(function (mutations) {
      for (var i = 0; i < mutations.length; i++) {
        if (mutations[i].addedNodes.length) {
          for (var j = 0; j < mutations[i].addedNodes.length; j++) {
            if (containsDiscountNinjaCard(mutations[i].addedNodes[j])) {
              pendingCollectionCards = true;
            }
          }
          schedule();
          return;
        }
      }
    }).observe(document.body, { childList: true, subtree: true });
  }

  /* ------------------------------------------------------------------ API */

  window.VIPPricing = {
    /** True once Discount Ninja has replaced at least one price. */
    isDiscounted: function () {
      return !!document.querySelector('.' + REPLACED);
    },
    refresh: refresh,
    tagPrices: tagPrices
  };

  function init() {
    refresh();
    observe();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', function (event) {
    if (tagPrices(event.target)) notifyDiscountNinja();
    syncNote();
  });
})();
